import React from "react";

import { useSaveActionsContext } from "context/save";

import useCategoryApi from "hooks/api/private/useCategoryApi";
import useMaterialApi from "hooks/api/private/useMaterialApi";
import useMaterialCategoryApi from "hooks/api/private/useMaterialCategoryApi";
import useProductApi from "hooks/api/private/useProductApi";
import useProductMaterialApi from "hooks/api/private/useProductMaterialApi";
import useProductMaterialCategoryApi from "hooks/api/private/useProductMaterialCategoryApi";

import { MaterialListItem } from "types/api/material";

import { createAdvancedListRequestFromFilter, filterEq, filterLike } from "utils/utils";

export type CategoryOption = {
    id: string;
    name: string;
};

function resolveFinalChecked(
    materialId: string,
    pendingChanges: Record<string, boolean>,
    categoryFullyLinked: boolean,
    categoryHadMaterialSelection: boolean,
    hadRow: boolean,
): boolean {
    if (pendingChanges[materialId] !== undefined) {
        return pendingChanges[materialId];
    }

    if (categoryFullyLinked) {
        return true;
    }

    if (!categoryHadMaterialSelection) {
        return false;
    }

    return hadRow;
}

export type ProductMaterialSelectionHook = {
    categories: Array<CategoryOption>;
    activeCategory: CategoryOption | null;
    selectCategory: (category: CategoryOption) => void;
    searchTerm: string;
    setSearchTerm: (value: string) => void;
    visibleMaterials: Array<MaterialListItem>;
    loading: boolean;
    isChecked: (materialId: string) => boolean;
    toggleMaterial: (materialId: string) => void;
    allVisibleChecked: boolean;
    toggleSelectAllVisible: () => void;
    hasSelection: (categoryId: string) => boolean;
};

function useProductMaterialSelectionForm({
    productId,
}: {
    productId: string;
}): ProductMaterialSelectionHook {
    const { getAllMaterial } = useMaterialApi();
    const { getAllMaterialCategory } = useMaterialCategoryApi();
    const { getAllProductMaterial, createProductMaterial, deleteProductMaterial } =
        useProductMaterialApi();
    const {
        getAllProductMaterialCategory,
        createProductMaterialCategory,
        deleteProductMaterialCategory,
    } = useProductMaterialCategoryApi();
    const { getOneProduct } = useProductApi();
    const { getOneCategory } = useCategoryApi();

    const { addSaveAction } = useSaveActionsContext();

    const [type, setType] = React.useState<"indoor" | "outdoor" | null>(null);

    const [categories, setCategories] = React.useState<Array<CategoryOption>>([]);
    const [activeCategory, setActiveCategory] = React.useState<CategoryOption | null>(null);
    const [searchTerm, setSearchTerm] = React.useState<string>("");

    // materials of every category the admin has opened this session, keyed by category id
    const [materialsByCategory, setMaterialsByCategory] = React.useState<
        Record<string, Array<MaterialListItem>>
    >({});
    const [loading, setLoading] = React.useState<boolean>(false);

    // material_id -> bo_products_to_material row id, as persisted before this session
    const [originalSelections, setOriginalSelections] = React.useState<Record<string, string>>({});

    // material_category_id -> bo_products_to_material_category row id ("tutti i materiali")
    const [originalCategoryLinks, setOriginalCategoryLinks] = React.useState<
        Record<string, string>
    >({});

    // material_category_id of every category with at least one persisted material selection
    const [curatedCategoryIds, setCuratedCategoryIds] = React.useState<Set<string>>(new Set());

    // material_id -> desired checked state, only set once the admin actually toggles it
    const [pendingChanges, setPendingChanges] = React.useState<Record<string, boolean>>({});

    const fetchedCategoryIds = React.useRef<Set<string>>(new Set());

    React.useEffect(() => {
        const [apiCall, abortController] = getOneProduct({ id: productId });

        apiCall.then((product) => {
            const [categoryApiCall] = getOneCategory({ id: product.primary_category_id });

            categoryApiCall.then((category) => setType(category.type));
        });

        return () => abortController.abort();
    }, [getOneProduct, getOneCategory, productId]);

    const refreshSelections = React.useCallback(() => {
        const [materialsApiCall, materialsAbortController] = getAllProductMaterial(
            createAdvancedListRequestFromFilter(
                filterEq("product_to_material.product_id", productId),
            ),
        );

        const [categoriesApiCall, categoriesAbortController] = getAllProductMaterialCategory(
            createAdvancedListRequestFromFilter(
                filterEq("product_to_material_category.product_id", productId),
            ),
        );

        materialsApiCall.then((res) => {
            const selections: Record<string, string> = {};
            const curatedIds = new Set<string>();

            res.content.forEach((item) => {
                selections[item.material_id] = item.id;

                if (item.material_category_id !== null) {
                    curatedIds.add(item.material_category_id);
                }
            });

            setOriginalSelections(selections);
            setCuratedCategoryIds(curatedIds);
        });

        categoriesApiCall.then((res) => {
            const links: Record<string, string> = {};

            res.content.forEach((item) => {
                links[item.material_category_id] = item.id;
            });

            setOriginalCategoryLinks(links);
        });

        return {
            abort: () => {
                materialsAbortController.abort();
                categoriesAbortController.abort();
            },
        };
    }, [getAllProductMaterial, getAllProductMaterialCategory, productId]);

    React.useEffect(() => {
        const request = refreshSelections();

        return () => request.abort();
    }, [refreshSelections]);

    React.useEffect(() => {
        if (type === null) {
            return;
        }

        const [apiCall, abortController] = getAllMaterialCategory(
            createAdvancedListRequestFromFilter(
                filterLike("material_category.type", type),
                "material_category.graphical_order,ASC",
            ),
        );

        apiCall.then((res) => {
            setCategories(
                res.content.map((category) => ({ id: category.id, name: category.name })),
            );
        });

        return () => abortController.abort();
    }, [getAllMaterialCategory, type]);

    const fetchCategoryMaterials = React.useCallback(
        (category: CategoryOption) => {
            if (fetchedCategoryIds.current.has(category.id)) {
                return;
            }

            fetchedCategoryIds.current.add(category.id);

            setLoading(true);

            const [apiCall] = getAllMaterial(
                createAdvancedListRequestFromFilter(
                    filterEq("material.material_category_id", category.id),
                    "material_languages.name,ASC",
                ),
            );

            apiCall.then((res) => {
                setMaterialsByCategory((prevState) => ({
                    ...prevState,
                    [category.id]: res.content,
                }));

                setLoading(false);
            });
        },
        [getAllMaterial],
    );

    const selectCategory = React.useCallback(
        (category: CategoryOption) => {
            setActiveCategory(category);
            setSearchTerm("");
            fetchCategoryMaterials(category);
        },
        [fetchCategoryMaterials],
    );

    React.useEffect(() => {
        if (activeCategory === null && categories.length > 0) {
            selectCategory(categories[0]);
        }
    }, [activeCategory, categories, selectCategory]);

    // Prefetch materials for curated categories that still miss the product-category link,
    // so save can create "tutti i materiali" without requiring the admin to open each one
    React.useEffect(() => {
        curatedCategoryIds.forEach((categoryId) => {
            if (originalCategoryLinks[categoryId] !== undefined) {
                return;
            }

            const category = categories.find((item) => item.id === categoryId);

            if (category) {
                fetchCategoryMaterials(category);
            }
        });
    }, [curatedCategoryIds, originalCategoryLinks, categories, fetchCategoryMaterials]);

    const isCategoryFullyLinked = React.useCallback(
        (categoryId: string): boolean => {
            return originalCategoryLinks[categoryId] !== undefined;
        },
        [originalCategoryLinks],
    );

    const categoryHadMaterialSelection = React.useCallback(
        (categoryId: string): boolean => {
            return curatedCategoryIds.has(categoryId);
        },
        [curatedCategoryIds],
    );

    const isChecked = React.useCallback(
        (materialId: string): boolean => {
            if (pendingChanges[materialId] !== undefined) {
                return pendingChanges[materialId];
            }

            if (!activeCategory) {
                return false;
            }

            if (isCategoryFullyLinked(activeCategory.id)) {
                return true;
            }

            if (categoryHadMaterialSelection(activeCategory.id)) {
                return originalSelections[materialId] !== undefined;
            }

            return false;
        },
        [
            pendingChanges,
            activeCategory,
            isCategoryFullyLinked,
            categoryHadMaterialSelection,
            originalSelections,
        ],
    );

    const toggleMaterial = React.useCallback(
        (materialId: string) => {
            setPendingChanges((prevState) => ({
                ...prevState,
                [materialId]: !isChecked(materialId),
            }));
        },
        [isChecked],
    );

    const visibleMaterials: Array<MaterialListItem> = React.useMemo(() => {
        if (!activeCategory) {
            return [];
        }

        const materials = materialsByCategory[activeCategory.id] ?? [];

        if (searchTerm.trim() === "") {
            return materials;
        }

        const needle = searchTerm.trim().toLowerCase();

        return materials.filter((material) => (material.name ?? "").toLowerCase().includes(needle));
    }, [activeCategory, materialsByCategory, searchTerm]);

    const allCategoryMaterials = React.useMemo(() => {
        if (!activeCategory) {
            return [];
        }

        return materialsByCategory[activeCategory.id] ?? [];
    }, [activeCategory, materialsByCategory]);

    const allVisibleChecked = React.useMemo(() => {
        return (
            allCategoryMaterials.length > 0 &&
            allCategoryMaterials.every((material) => isChecked(material.id))
        );
    }, [allCategoryMaterials, isChecked]);

    const toggleSelectAllVisible = React.useCallback(() => {
        const nextChecked = !allVisibleChecked;

        setPendingChanges((prevState) => {
            const newState = { ...prevState };

            allCategoryMaterials.forEach((material) => {
                newState[material.id] = nextChecked;
            });

            return newState;
        });
    }, [allVisibleChecked, allCategoryMaterials]);

    const wouldBeLinked = React.useCallback(
        (categoryId: string): boolean => {
            const materials = materialsByCategory[categoryId] ?? [];

            const touched = materials.some((material) => pendingChanges[material.id] !== undefined);

            if (!touched) {
                return (
                    curatedCategoryIds.has(categoryId) ||
                    originalCategoryLinks[categoryId] !== undefined
                );
            }

            const categoryFullyLinked = isCategoryFullyLinked(categoryId);
            const hadMaterialSelection = categoryHadMaterialSelection(categoryId);

            return materials.some((material) =>
                resolveFinalChecked(
                    material.id,
                    pendingChanges,
                    categoryFullyLinked,
                    hadMaterialSelection,
                    originalSelections[material.id] !== undefined,
                ),
            );
        },
        [
            materialsByCategory,
            pendingChanges,
            curatedCategoryIds,
            originalCategoryLinks,
            isCategoryFullyLinked,
            categoryHadMaterialSelection,
            originalSelections,
        ],
    );

    const buildDiffForCategory = React.useCallback(
        (categoryId: string): Array<Promise<unknown>> => {
            const materials = materialsByCategory[categoryId] ?? [];

            if (materials.length === 0) {
                return [];
            }

            const touched = materials.some((material) => pendingChanges[material.id] !== undefined);
            const categoryFullyLinked = isCategoryFullyLinked(categoryId);
            const hadMaterialSelection = categoryHadMaterialSelection(categoryId);
            const hadCategoryLink = originalCategoryLinks[categoryId] !== undefined;

            const promises: Array<Promise<unknown>> = [];

            const finalCheckedByMaterial: Array<{
                material: MaterialListItem;
                finalChecked: boolean;
                hadRow: boolean;
            }> = materials.map((material) => {
                const hadRow = originalSelections[material.id] !== undefined;
                const finalChecked = resolveFinalChecked(
                    material.id,
                    pendingChanges,
                    categoryFullyLinked,
                    hadMaterialSelection,
                    hadRow,
                );

                return { material, finalChecked, hadRow };
            });

            if (touched) {
                finalCheckedByMaterial.forEach(({ material, finalChecked, hadRow }) => {
                    if (finalChecked && !hadRow) {
                        const [apiCall] = createProductMaterial({
                            product_id: productId,
                            material_id: material.id,
                        });

                        promises.push(apiCall);
                    }

                    if (!finalChecked && hadRow) {
                        const [apiCall] = deleteProductMaterial({
                            id: originalSelections[material.id],
                        });

                        promises.push(apiCall);
                    }
                });
            }

            const allFinalChecked = finalCheckedByMaterial.every(
                ({ finalChecked }) => finalChecked,
            );

            if (allFinalChecked && !hadCategoryLink) {
                const [apiCall] = createProductMaterialCategory({
                    product_id: productId,
                    material_category_id: categoryId,
                });

                promises.push(apiCall);
            }

            if (touched && !allFinalChecked && hadCategoryLink) {
                const [apiCall] = deleteProductMaterialCategory({
                    id: originalCategoryLinks[categoryId],
                });

                promises.push(apiCall);
            }

            return promises;
        },
        [
            materialsByCategory,
            pendingChanges,
            isCategoryFullyLinked,
            categoryHadMaterialSelection,
            originalSelections,
            originalCategoryLinks,
            createProductMaterial,
            deleteProductMaterial,
            createProductMaterialCategory,
            deleteProductMaterialCategory,
            productId,
        ],
    );

    React.useEffect(() => {
        addSaveAction("useProductMaterialSelectionForm", () => {
            const promises = Object.keys(materialsByCategory).flatMap(buildDiffForCategory);

            return Promise.all(promises).then((results) => {
                // everything just saved is now the new baseline: drop pending overrides
                // and re-fetch the authoritative state, so a later save in the same
                // session diffs against what's actually in the database
                setPendingChanges({});
                refreshSelections();

                return results;
            });
        });
    }, [addSaveAction, materialsByCategory, buildDiffForCategory, refreshSelections]);

    return React.useMemo(
        (): ProductMaterialSelectionHook => ({
            categories,
            activeCategory,
            selectCategory,
            searchTerm,
            setSearchTerm,
            visibleMaterials,
            loading,
            isChecked,
            toggleMaterial,
            allVisibleChecked,
            toggleSelectAllVisible,
            hasSelection: wouldBeLinked,
        }),
        [
            categories,
            activeCategory,
            selectCategory,
            searchTerm,
            visibleMaterials,
            loading,
            wouldBeLinked,
            isChecked,
            toggleMaterial,
            allVisibleChecked,
            toggleSelectAllVisible,
        ],
    );
}

export default useProductMaterialSelectionForm;
